import { useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import { useParams } from "react-router-dom";
import { PageHeader } from "../component/PageHeader";
import ResponsivePagination from "react-responsive-pagination";
import { useWallletContext } from "../hook/useWalletContext";
import { useRecoilState } from "recoil";
import { ShowProgress } from "../states/ShowProgress";
import { StakingService } from "../openapi";
import { toast } from "react-toastify";
import { TOAST_ID } from "../common/const";
import { StakingItem } from "../component/StakingItem";
import * as yup from "yup";
import { validateFields } from "../common/helper";

const PAGE_LIMIT = 10;

export const Staking = () => {
  const { apy } = useParams();

  const { theme } = useWallletContext();

  const [, setShowProgress] = useRecoilState(ShowProgress);

  const [address, setAddress] = useState<string>("");
  const [amountFrom, setAmountFrom] = useState<string>("");
  const [amountTo, setAmountTo] = useState<string>("");
  const [stakedAtFrom, setStakedAtFrom] = useState<Date | null>();
  const [stakedAtTo, setStakedAtTo] = useState<Date | null>();

  const [page, setPage] = useState<number>(1);
  const [total, setTotal] = useState<number>(0);

  const [stakings, setStakings] = useState<any[]>([]);

  useEffect(() => {
    getStakings();
  }, [page, apy]);

  const handleSearch = async () => {
    try {
      await validateFields(
        yup.object().shape({
          amountFrom: yup
            .string()
            .matches(
              /^((\d*)|(\d+\.\d*)[^0\D])$/g,
              "Please input correct amount."
            ),
          amountTo: yup
            .string()
            .matches(
              /^((\d*)|(\d+\.\d*)[^0\D])$/g,
              "Please input correct amount."
            ),
        }),
        ["amountFrom", "amountTo"],
        [amountFrom, amountTo]
      );
    } catch (err: any) {
      toast.warn(err.message, { theme: theme });
      return;
    }

    if (page === 1) {
      getStakings();
    } else {
      setPage(1);
    }
  };

  const getStakings = () => {
    setShowProgress(true);

    StakingService.listStakings(
      parseInt(apy ?? "0"),
      (page - 1) * PAGE_LIMIT,
      PAGE_LIMIT,
      address,
      amountFrom ? parseFloat(amountFrom) : undefined,
      amountTo ? parseFloat(amountTo) : undefined,
      stakedAtFrom?.getTime() ?? undefined,
      stakedAtTo?.getTime() ?? undefined
    )
      .then((res) => {
        setShowProgress(false);

        setStakings(res[0]);
        setTotal(res[1]);
      })
      .catch((err) => {
        console.log(err);

        setShowProgress(false);
        toast.error("Getting stakings has been failed.", {
          theme: theme,
          toastId: TOAST_ID.GETTING_STAKING,
        });
      });
  };

  return (
    <>
      <PageHeader title={`Staking (APY : ${apy})`} />

      <div className="box">
        <div className="box-body">
          <div className="flex">
            <div className="block">
              <label
                htmlFor="inline-input-label-with-helper-text"
                className="ti-form-label text-md"
              >
                Address
              </label>
              <input
                type="text"
                id="inline-input-label-with-helper-text"
                className="ti-form-input"
                placeholder="Input the address"
                aria-describedby="hs-inline-input-helper-text"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
              />
            </div>
            <div className="flex mx-auto">
              <div className="block mr-3">
                <label
                  htmlFor="inline-input-label-with-helper-text"
                  className="ti-form-label text-md"
                >
                  Amount
                </label>
                <input
                  type="text"
                  id="inline-input-label-with-helper-text"
                  className="ti-form-input"
                  placeholder="Amount"
                  aria-describedby="hs-inline-input-helper-text"
                  value={amountFrom}
                  onChange={(e) => setAmountFrom(e.target.value)}
                />
              </div>
              <div className="flex">
                <label
                  htmlFor="inline-input-label-with-helper-text"
                  className="ti-form-label text-md mt-auto mb-3"
                >
                  ~
                </label>
              </div>
              <div className="flex ml-3">
                <input
                  type="text"
                  id="inline-input-label-with-helper-text"
                  className="ti-form-input mt-auto"
                  placeholder="Amount"
                  aria-describedby="hs-inline-input-helper-text"
                  value={amountTo}
                  onChange={(e) => setAmountTo(e.target.value)}
                />
              </div>
            </div>
            <div className="flex mr-auto">
              <div className="block mr-3">
                <label
                  htmlFor="inline-input-label-with-helper-text"
                  className="ti-form-label text-md"
                >
                  Staked At
                </label>
                <div className="flex rounded-sm overflow-auto">
                  <div className="px-4 inline-flex items-center min-w-fit ltr:rounded-l-sm rtl:rounded-r-sm border ltr:border-r-0 rtl:border-l-0 border-gray-200 bg-gray-50 dark:bg-black/20 dark:border-white/10">
                    <span className="text-sm text-gray-500 dark:text-white/70">
                      <i className="ri ri-calendar-line"></i>
                    </span>
                  </div>

                  <DatePicker
                    className="ti-form-input ltr:rounded-l-none rtl:rounded-r-none focus:z-10"
                    selected={stakedAtFrom}
                    onChange={(date: Date | null) => setStakedAtFrom(date)}
                    timeInputLabel="Time:"
                    dateFormat="MM/dd/yyyy h:mm aa"
                    showTimeInput
                  />
                </div>
              </div>
              <div className="flex">
                <label
                  htmlFor="inline-input-label-with-helper-text"
                  className="ti-form-label text-md mt-auto mb-3"
                >
                  ~
                </label>
              </div>
              <div className="flex ml-3">
                <div className="flex rounded-sm overflow-auto mt-auto">
                  <div className="px-4 inline-flex items-center min-w-fit ltr:rounded-l-sm rtl:rounded-r-sm border ltr:border-r-0 rtl:border-l-0 border-gray-200 bg-gray-50 dark:bg-black/20 dark:border-white/10">
                    <span className="text-sm text-gray-500 dark:text-white/70">
                      <i className="ri ri-calendar-line"></i>
                    </span>
                  </div>

                  <DatePicker
                    className="ti-form-input ltr:rounded-l-none rtl:rounded-r-none focus:z-10"
                    selected={stakedAtTo}
                    onChange={(date: Date | null) => setStakedAtTo(date)}
                    timeInputLabel="Time:"
                    dateFormat="MM/dd/yyyy h:mm aa"
                    showTimeInput
                  />
                </div>
              </div>
            </div>
            <button
              type="button"
              className="ti-btn ti-btn-primary h-46 mt-auto"
              onClick={() => handleSearch()}
            >
              Search
            </button>
          </div>
          <div className="mt-4 overflow-auto">
            <table className="ti-custom-table ti-custom-table-head">
              <thead className="">
                <tr>
                  <th scope="col text-center">No</th>
                  <th scope="col text-center">Address</th>
                  <th scope="col text-center">Amount</th>
                  <th scope="col text-center">Reward</th>
                  <th scope="col text-center">Staked At</th>
                  <th scope="col text-center">Total Withdrawal</th>
                  <th scope="col text-center">Total Claimed Reward</th>
                  <th scope="col text-center">Last Withdrew At</th>
                  <th scope="col text-center">Status</th>
                </tr>
              </thead>
              <tbody>
                {stakings.map((item: any, index: number) => {
                  return (
                    <StakingItem
                      item={item}
                      apy={parseInt(apy ?? "0")}
                      index={index}
                      key={index}
                    />
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="flex mt-2">
            <div className="ml-auto w-500">
              <ResponsivePagination
                current={page}
                total={Math.ceil(total / PAGE_LIMIT)}
                onPageChange={(page) => {
                  setPage(page);
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
